// 피아노울림 공용 디자인 토큰 — "건반(Keybed)" 테마
// 흑건(잉크 블랙)을 주 액션 색, 백건(아이보리)을 바탕색으로 쓰고
// 골드는 브랜드 각인처럼 "선택됨/포인트" 표시에만 아껴 쓴다.
import { createTheme } from '@mui/material';

export const piano = {
  ink: '#111111',          // 흑건 — 주 액션/텍스트
  inkSoft: '#5F5C54',      // 보조 텍스트
  muted: '#8A877E',        // 힌트/캡션
  ivory: '#FAFAF7',        // 백건 — 앱 바탕
  paper: '#FFFFFF',        // 카드/다이얼로그 표면
  keyline: '#E8E5DC',      // 옅은 구분선
  keylineStrong: '#D9D6CC',// 진한 구분선/입력 테두리
  gold: '#C9A227',         // 골드 포인트 (라이트)
  goldBright: '#D4AF37',   // 골드 포인트 (다크 — 어두운 바탕 대비 확보)
  darkBg: '#121212',
  darkPaper: '#1E1E1E',
};

export function buildPianoTheme(darkMode) {
  return createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      ...(darkMode
        ? {
            primary: { main: piano.ivory, contrastText: piano.ink },
            secondary: { main: piano.goldBright, contrastText: piano.ink },
            background: { default: piano.darkBg, paper: piano.darkPaper },
          }
        : {
            primary: { main: piano.ink, contrastText: piano.ivory },
            secondary: { main: piano.gold, contrastText: piano.ink },
            background: { default: piano.ivory, paper: piano.paper },
            text: { primary: piano.ink, secondary: piano.inkSoft },
            divider: piano.keyline,
          }),
    },
    // 주의: shape.borderRadius를 올리면 앱 전역 sx의 숫자 borderRadius(×배수)가 전부 재해석되므로
    // 기본값(4)을 유지하고, 곡률은 컴포넌트 styleOverrides의 절대 px로만 지정한다.
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 10 },
        },
      },
      // 모바일에서 MUI 기본 여백(paper margin 32 + 콘텐츠 좌우 패딩 24)이 폭을
      // 과도하게 잠식해 짧은 문장도 줄바꿈되므로 600px 미만에서는 16px로 좁힌다.
      // fullScreen 다이얼로그는 여백/곡률 대상에서 제외한다.
      MuiDialog: {
        styleOverrides: {
          paper: {
            '&:not(.MuiDialog-paperFullScreen)': {
              borderRadius: 16,
              '@media (max-width:599.95px)': {
                margin: 16,
                maxHeight: 'calc(100% - 32px)',
              },
            },
          },
          paperFullWidth: {
            '&:not(.MuiDialog-paperFullScreen)': {
              '@media (max-width:599.95px)': {
                width: 'calc(100% - 32px)',
              },
            },
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            '@media (max-width:599.95px)': { paddingLeft: 16, paddingRight: 16 },
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            '@media (max-width:599.95px)': { paddingLeft: 16, paddingRight: 16 },
          },
        },
      },
      // Select 팝업/프로필 메뉴 공통. maxHeight로 시간 선택처럼 옵션이 많은
      // 드롭다운이 화면 전체로 늘어나는 것을 막는다(선택 항목으로 자동 스크롤됨).
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 12,
            border: `1px solid ${darkMode ? 'rgba(250, 250, 247, 0.12)' : piano.keyline}`,
            boxShadow: darkMode
              ? '0 8px 24px rgba(0, 0, 0, 0.5)'
              : '0 8px 24px rgba(17, 17, 17, 0.12)',
            maxHeight: 320,
          },
          list: { padding: 6 },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            '&.Mui-selected': {
              backgroundColor: darkMode
                ? 'rgba(212, 175, 55, 0.18)'
                : 'rgba(201, 162, 39, 0.14)',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: darkMode
                  ? 'rgba(212, 175, 55, 0.26)'
                  : 'rgba(201, 162, 39, 0.2)',
              },
              '&::after': {
                content: '"✓"',
                marginLeft: 'auto',
                paddingLeft: 12,
                color: darkMode ? piano.goldBright : piano.gold,
              },
            },
          },
        },
      },
    },
  });
}
